import React, { useState } from 'react';
import { Box, Typography, Chip, Avatar, Collapse, Grid, IconButton } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Payment as PaymentIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';

const PaymentItem = ({ payment }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded':
      case 'success':
        return <CheckCircleIcon sx={{ color: '#22c55e' }} />;
      case 'pending':
        return <ScheduleIcon sx={{ color: '#f59e0b' }} />;
      case 'failed':
      case 'cancelled':
        return <ErrorIcon sx={{ color: '#ef4444' }} />;
      default:
        return <PaymentIcon sx={{ color: '#6b7280' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
      case 'success':
        return { bgcolor: '#22c55e', color: 'white' };
      case 'pending':
        return { bgcolor: '#f59e0b', color: 'white' };
      case 'failed':
      case 'cancelled':
        return { bgcolor: '#ef4444', color: 'white' };
      default:
        return { bgcolor: '#6b7280', color: 'white' };
    }
  };

  const formatAmount = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'succeeded':
        return 'Success';
      case 'pending':
        return 'Pending';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  return (
    // Container
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 1.5, bgcolor: 'white', overflow: 'hidden' }}>
      {/* Header Row */}
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': { bgcolor: '#f7fafc' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar sx={{
            width: 40,
            height: 40,
            bgcolor: payment.status === 'succeeded' ? '#f0fff4' :
                     payment.status === 'failed' || payment.status === 'cancelled' ? '#fed7d7' :
                     payment.status === 'pending' ? '#fef5e7' : '#f7fafc'
          }}>
            {getStatusIcon(payment.status)}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c', mb: 0.5 }}>
              {formatAmount(payment.amount, payment.currency)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#718096' }}>
              {payment.plan_name} • {formatDate(payment.created_at)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Typography variant="body2" sx={{ color: '#718096', fontFamily: 'monospace', fontSize: '0.75rem' }}>
            ID: {payment.id || payment._id}
          </Typography>
          <Chip
            label={getStatusLabel(payment.status)}
            size="small"
            sx={{
              fontWeight: 600,
              textTransform: 'capitalize',
              ...getStatusColor(payment.status),
              '&:hover': { opacity: 0.9 }
            }}
            icon={<KeyboardArrowDownIcon sx={{ fontSize: 16, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
          />
        </Box>
      </Box>

      {/* Details */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ bgcolor: '#f7fafc', p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2d3748', mb: 2 }}>
                Payment Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>Amount:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatAmount(payment.amount, payment.currency)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>Plan:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{payment.plan_name || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>Billing Cycle:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{payment.billing_cycle || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>Status:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{getStatusLabel(payment.status)}</Typography>
                </Box>
                {payment.razorpay_invoice_id && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#718096' }}>Invoice ID:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                      {payment.razorpay_invoice_id}
                      </Typography>
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(payment.razorpay_invoice_id)} sx={{ p: 0.5, '&:hover': { bgcolor: '#e2e8f0' } }}>
                        <ContentCopyIcon sx={{ fontSize: 16, color: '#718096' }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}
                {payment.razorpay_payment_id && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>Payment ID:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                    {payment.razorpay_payment_id}
                    </Typography>
                  </Box>
                )}
                {payment.razorpay_subscription_id && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#718096' }}>Subscription ID:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                      {payment.razorpay_subscription_id}
                      </Typography>
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(payment.razorpay_subscription_id)} sx={{ p: 0.5, '&:hover': { bgcolor: '#e2e8f0' } }}>
                        <ContentCopyIcon sx={{ fontSize: 16, color: '#718096' }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2d3748', mb: 2 }}>
                Timestamps
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>Created:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(payment.created_at)}</Typography>
                </Box>
                {payment.updated_at && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#718096' }}>Updated:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(payment.updated_at)}</Typography>
                  </Box>
                )}
                {payment.start_date && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#718096' }}>Start Date:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(payment.start_date)}</Typography>
                  </Box>
                )}
                {payment.end_date && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#718096' }}>End Date:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(payment.end_date)}</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};

export default PaymentItem;
